import { alpha, createTheme } from '@mui/material/styles';

// Accessible color palette with proper contrast ratios
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0B4FAE', // Blue 800 - high contrast
      light: '#1976D2',
      dark: '#013F78',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0176BD', // Purple 700
      light: '#9FC6FF',
      dark: '#013F78',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#2E7D32', // Green 800
      light: '#4CAF50',
      dark: '#1B5E20',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#ED6C02', // Orange 800
      light: '#FF9800',
      dark: '#E65100',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#C62828', // Red 800
      light: '#EF5350',
      dark: '#B71C1C',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0277BD', // Light Blue 800
      light: '#03A9F4',
      dark: '#01579B',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121', // Very dark gray for high contrast
      secondary: '#424242',
    },
    almathema: {
      sapphire: '#0B4FAE',
      deepSapphire: '#083B82',
      sunYellow: '#FFC400',
      babyBlueIce: '#9FC6FF',
      lightGray: '#E6E6E6',
      white: '#FFFFFF',
      sapphireOverlay95: alpha('#0B4FAE', 0.95),
      whiteOverlay12: alpha('#FFFFFF', 0.12),
      sunYellowHover: alpha('#FFC400', 0.88),
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: '2.5rem',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: '"Lato", "Helvetica Neue", "Arial", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Lato", "Helvetica Neue", "Arial", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Lato", "Helvetica Neue", "Arial", sans-serif',
      textTransform: 'none',
      fontWeight: 400,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          padding: '0.2rem 2rem',
          minHeight: 44, // Minimum touch target size
          '&:focus-visible': {
            outline: '3px solid #1565C0',
            outlineOffset: 2,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:focus-within': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1565C0',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1565C0',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1565C0',
            outlineOffset: 2,
          },
        },
      },
    },
  },
});
